// #viet_code
import React, { useState } from 'react';
import { Table, Button, Form, Row, Col, Badge } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Get, Post, Put, DeleteSingle } from '@app/utils/httpProvider';
import { MAINTENANCE_EVENT_MATERIAL_API, MATERIALCATALOG_API } from '@app/utils/apiUrl';
import { toast } from 'react-toastify';
import Select from 'react-select';

const MaintenanceEventMaterialTable = ({ eventId }) => {
    const queryClient = useQueryClient();
    const [isAdding, setIsAdding] = useState(false);
    const [editingItem, setEditingItem] = useState(null); // Trạng thái dòng đang sửa
    const [newMaterial, setNewMaterial] = useState({
        materialCatalogId: '',
        quantity: 1,
        unitPrice: 0,
        note: ''
    });

    // Lấy danh sách vật tư đã sử dụng thực tế
    const { data: materials, isLoading } = useQuery(
        ['maintenanceEventMaterials', eventId],
        () => Get(`${MAINTENANCE_EVENT_MATERIAL_API.GET_BY_EVENT}/${eventId}`),
        { enabled: !!eventId }
    );

    // Lấy danh mục vật tư
    const { data: catalogData } = useQuery(['materialCatalog'], () =>
        Get(MATERIALCATALOG_API.GET_PAGING, null, { PageIndex: 1, PageSize: 1000 })
    );
    const catalogOptions = catalogData?.data?.map(m => ({
        value: m.id,
        label: `${m.materialCode || m.code || ''} - ${m.materialName || m.name || ''}`
    })) || [];

    const addMutation = useMutation((item) => Post(MAINTENANCE_EVENT_MATERIAL_API.ADD_ITEM, null, item));
    const deleteMutation = useMutation((id) => DeleteSingle(`${MAINTENANCE_EVENT_MATERIAL_API.DELETE_ITEM}/${id}`));
    const updateMutation = useMutation((item) => Put(MAINTENANCE_EVENT_MATERIAL_API.UPDATE_ITEM, item.id, item));

    const handleAdd = () => {
        if (!newMaterial.materialCatalogId) {
            toast.warning('Vui lòng chọn vật tư');
            return;
        }
        addMutation.mutate({ ...newMaterial, assetMaintenanceEventId: eventId }, {
            onSuccess: () => {
                toast.success('Thêm vật tư thành công');
                queryClient.invalidateQueries(['maintenanceEventMaterials', eventId]);
                setIsAdding(false);
                setNewMaterial({ materialCatalogId: '', quantity: 1, unitPrice: 0, note: '' });
            }
        });
    };

    const handleDelete = (id) => {
        if (window.confirm('Bạn có chắc chắn muốn xóa vật tư này?')) {
            deleteMutation.mutate(id, {
                onSuccess: () => {
                    toast.success('Xóa vật tư thành công');
                    queryClient.invalidateQueries(['maintenanceEventMaterials', eventId]);
                }
            });
        }
    };

    const handleEditClick = (item) => { //1
        setEditingItem({ ...item });
        setIsAdding(false); // Tắt form thêm khi bấm sửa
    };

    const handleCancelEdit = () => {
        setEditingItem(null);
    };

    const handleSaveEdit = () => {
        if (!editingItem.materialCatalogId) {
            toast.warning('Vui lòng chọn vật tư');
            return;
        }
        updateMutation.mutate(editingItem, {
            onSuccess: () => {
                toast.success('Cập nhật vật tư thành công');
                queryClient.invalidateQueries(['maintenanceEventMaterials', eventId]);
                setEditingItem(null);
            }
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(amount || 0);
    };

    return (
        <div className="mt-2">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5><i className="fas fa-tools mr-2"></i>Chi tiết vật tư thay thế</h5>
            </div>

            <Table striped bordered hover responsive size="sm">
                <thead className="bg-light text-center">
                    <tr>
                        <th className="align-middle">STT</th>
                        <th className="align-middle">Vật tư / Linh kiện</th>
                        <th className="align-middle" style={{ width: '120px' }}>Số lượng</th>
                        <th className="align-middle">Đơn giá</th>
                        <th className="align-middle">Thành tiền</th>
                        <th className="align-middle">Ghi chú</th>
                        <th className="align-middle">Thao tác</th>
                    </tr>
                </thead>
                <tbody>
                    {materials?.map((item, index) => {
                        if (editingItem && editingItem.id === item.id) {
                            return (
                                <tr key={item.id} className="table-info">
                                    <td className="text-center">{index + 1}</td>
                                    <td>
                                        <Select
                                            options={catalogOptions}
                                            value={catalogOptions.find(opt => opt.value === editingItem.materialCatalogId)}
                                            onChange={(opt) => setEditingItem({ ...editingItem, materialCatalogId: opt.value })}
                                            placeholder="Chọn vật tư..."
                                            menuPortalTarget={document.body}
                                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 10050 }) }}
                                        />
                                    </td>
                                    <td>
                                        <Form.Control
                                            type="number" size="sm" min={0}
                                            value={editingItem.quantity}
                                            onChange={(e) => setEditingItem({ ...editingItem, quantity: Math.max(0, parseInt(e.target.value) || 0) })}
                                        />
                                    </td>
                                    <td>
                                        <Form.Control
                                            type="number" size="sm" min={0}
                                            value={editingItem.unitPrice}
                                            onChange={(e) => setEditingItem({ ...editingItem, unitPrice: Math.max(0, parseFloat(e.target.value) || 0) })}
                                        />
                                    </td>
                                    <td className="text-end align-middle">{formatCurrency((editingItem.quantity || 0) * (editingItem.unitPrice || 0))}</td>
                                    <td>
                                        <Form.Control
                                            type="text" size="sm"
                                            value={editingItem.note || ''}
                                            onChange={(e) => setEditingItem({ ...editingItem, note: e.target.value })}
                                        />
                                    </td>
                                    <td className="text-center" style={{ minWidth: '90px' }}>
                                        <Button variant="success" size="sm" onClick={handleSaveEdit} title="Lưu" className="mr-2">
                                            <i className="fas fa-save"></i>
                                        </Button>
                                        <Button variant="secondary" size="sm" onClick={handleCancelEdit} title="Hủy">
                                            <i className="fas fa-times"></i>
                                        </Button>
                                    </td>
                                </tr>
                            );
                        }

                        return (
                            <tr key={item.id}>
                                <td className="text-center">{index + 1}</td>
                                <td>
                                    <strong>{item.materialCode}</strong> - {item.materialName}
                                </td>
                                <td className="text-center fw-bold text-primary">
                                    {item.quantity}
                                </td>
                                <td className="text-end">{formatCurrency(item.unitPrice)}</td>
                                <td className="text-end fw-bold">{formatCurrency(item.quantity * (item.unitPrice || 0))}</td>
                                <td>{item.note}</td>
                                <td className="text-center" style={{ minWidth: '90px' }}>
                                    <Button variant="link" className="text-primary p-0 mr-3" onClick={() => handleEditClick(item)} title="Sửa">
                                        <i className="fas fa-edit"></i>
                                    </Button>
                                    <Button variant="link" className="text-danger p-0" onClick={() => handleDelete(item.id)} title="Xóa">
                                        <i className="fas fa-trash-alt"></i>
                                    </Button>
                                </td>
                            </tr>
                        );
                    })}

                    {isAdding && (
                        <tr className="table-warning">
                            <td>+</td>
                            <td>
                                <Select
                                    options={catalogOptions}
                                    onChange={(opt) => setNewMaterial({ ...newMaterial, materialCatalogId: opt.value })}
                                    placeholder="Chọn vật tư..."
                                    menuPortalTarget={document.body}
                                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 10050 }) }}
                                />
                            </td>
                            <td>
                                <Form.Control
                                    type="number" size="sm" min={0}
                                    value={newMaterial.quantity}
                                    onChange={(e) => setNewMaterial({ ...newMaterial, quantity: Math.max(0, parseInt(e.target.value) || 0) })}
                                />
                            </td>
                            <td>
                                <Form.Control
                                    type="number" size="sm" min={0}
                                    value={newMaterial.unitPrice}
                                    onChange={(e) => setNewMaterial({ ...newMaterial, unitPrice: Math.max(0, parseFloat(e.target.value) || 0) })}
                                />
                            </td>
                            <td className="text-end align-middle">{formatCurrency(newMaterial.quantity * newMaterial.unitPrice)}</td>
                            <td>
                                <Form.Control
                                    type="text" size="sm"
                                    value={newMaterial.note}
                                    onChange={(e) => setNewMaterial({ ...newMaterial, note: e.target.value })}
                                />
                            </td>
                            <td className="text-center" style={{ minWidth: '90px' }}>
                                <Button variant="success" size="sm" onClick={handleAdd} title="Lưu" className="mr-2">
                                    <i className="fas fa-save"></i>
                                </Button>
                                <Button variant="secondary" size="sm" onClick={() => setIsAdding(false)} title="Hủy">
                                    <i className="fas fa-times"></i>
                                </Button>
                            </td>
                        </tr>
                    )}
                </tbody>
                {materials?.length > 0 && (
                    <tfoot>
                        <tr className="bg-light fw-bold">
                            <td colSpan={4} className="text-end">Tổng cộng chi phí vật tư:</td>
                            <td className="text-end text-primary" style={{ fontSize: '1.1rem' }}>
                                {formatCurrency(materials.reduce((sum, item) => sum + (item.quantity * (item.unitPrice || 0)), 0))}
                            </td>
                            <td colSpan={2}></td>
                        </tr>
                    </tfoot>
                )}
            </Table>

            {!isAdding && !editingItem && (
                <div className="text-center mt-2">
                    <Button variant="outline-primary" size="sm" onClick={() => setIsAdding(true)}>
                        <i className="fas fa-plus mr-1"></i> Thêm vật tư thực tế
                    </Button>
                </div>
            )}

        </div>
    );
};

export default MaintenanceEventMaterialTable;
